import type { DashboardCalculation, DashboardFieldRole, DashboardWidgetType } from './types';
import { MAX_TOOLTIP_MEASURES } from './services/widgetDataContract';

/**
 * The catalogue of visualisations and the field wells each one declares
 * (PRD - Dashboards, Apéndice A · SL-1664).
 *
 * **The catalogue is data, not code.** Every rule the panel, the card and the
 * validation need — what a well accepts, whether it takes several fields, and
 * whether it is required — is declared here and read at runtime. Nothing
 * downstream branches on the widget's type, which is what let the thirteen
 * types land as thirteen rows instead of thirteen code paths.
 *
 * Ported from the design prototype's `CHANNELS_BY_TYPE`
 * (`Design/src/data/analyticsTypes.ts`), including its two traps:
 *
 * - **Well keys are persisted.** A saved widget stores its fields under these
 *   strings, so renaming one would leave existing widgets unable to draw. The
 *   prototype states the same rule over the same keys.
 * - **`pie` and `donut` key their legend `category`, not `legend`.** That is
 *   where the prototype's saved pies keep their dimension; the label above it
 *   still reads "Legend". Vocabulary is the label's job, not the key's.
 */

/**
 * Why a well the user filled in is not on the canvas.
 *
 * Codes and not sentences, like every "why is this off" answer of the module:
 * the label is the i18n leaf `widget.unavailable.<reason>`.
 */
export type WellUnavailableReason =
  // The Line's secondary axis while small multiples are on: the endpoint does
  // not repeat it per panel (`WidgetPanel`, SL-1826), so the panels never draw it.
  | 'secondaryAxisNotInPanels'
  // A cartesian's tooltips while small multiples are on: the endpoint answers
  // them for the whole chart only, never per panel, so no panel can show them.
  | 'tooltipsNotInPanels';

/**
 * Why a whole visualisation cannot be picked.
 *
 * Same idea as `WellUnavailableReason`, one level up: codes, with the label at
 * `widget.unavailable.<reason>`. Both come from the data (`typeAvailability`):
 * since the funnel got its chart type (SL-1825) no type is blocked by
 * declaration.
 */
export type TypeUnavailableReason = 'noColumns' | 'needsNumericColumns';

/**
 * When a well is not drawn.
 *
 * `notWithWell` depends on the rest of the widget: the well is drawn until
 * another one (`wellKey`) holds a field. Resolved by `wellAvailability` against
 * the widget's wells.
 */
export type WellDrawRule = { kind: 'notWithWell'; wellKey: string; reason: WellUnavailableReason };

/**
 * A well: one slot of a visualisation that fields are placed into.
 *
 * `key` is what `DashboardWidget.wells` is keyed by, and it is also the i18n
 * leaf under `widget.well.*` — the same trick the blocked reasons use, so the
 * label of a well never has to be looked up in a second table.
 *
 * `accepts: 'any'` exists for exactly one well — the Table's single column
 * list, which takes dimensions and measures mixed. It is not a loophole: it is
 * what that visualisation is.
 */
export interface DashboardWell {
  key: string;
  /**
   * The i18n leaf that names it, when it is not the key.
   *
   * The key is normally the label's leaf too, which is what keeps a well's name
   * from needing a second lookup table. The exception is a well whose **stored**
   * key and whose **shown** name disagree: `pie` and `donut` keep their legend
   * under `category`, because that is where saved widgets have it, but the
   * label above it has to read "Legend" — calling it "Category" would name it
   * after the storage decision rather than after what it does.
   */
  labelKey?: string;
  /** Dimensions, measures, or — only for the Table's columns — both. */
  accepts: DashboardFieldRole | 'any';
  /** Whether more than one field can be placed here. */
  multiple: boolean;
  /** Whether the widget is invalid while this well is empty. */
  required: boolean;
  /**
   * The i18n leaf of the line under the control, when the well needs one of its
   * own.
   *
   * Every well gets a hint: with nothing declared here the panel falls back to
   * one per kind — what a measure is, what a dimension is. What earns an
   * explicit hint is a well that carries a **rule the user cannot deduce**, like
   * a scatter collapsing into one point without Values.
   */
  hint?: string;
  /**
   * Whether filling this well changes what the rest of the widget draws.
   *
   * True on the Legend of a cartesian: with one, the series come from its values
   * and only the first measure is drawn (PRD §7), and the panel has to say so —
   * dropping the others in silence reads as the chart having lost data.
   *
   * Declared per well and not derived from the key, because the scatter's legend
   * is keyed the same and the warning does **not** apply to it: there the two
   * measures are the axes.
   */
  legendNote?: boolean;
  /**
   * Whether the well offers only numeric columns (SL-1665).
   *
   * A measure well normally takes any column — over text the calculation falls
   * back to Count — so this is the exception, declared where it applies: the
   * scatter's two axes, which place points on a plane and have nothing to plot
   * over a name. `columnFitsWell` is the one place that reads it, and both the
   * pickers and the type picker's availability go through that.
   */
  numericOnly?: boolean;
  /**
   * Whether a type change may drop a field from another well here (SL-1794).
   *
   * A field the new type has no well for under its own key moves to the first
   * empty well that takes its role, so KPI → Table keeps the measure and
   * Bar → Pie keeps both axes. `false` on the wells that only **decorate** a
   * chart — tooltips, small multiples, the play axis, the bubble size, the
   * second axis: landing a moved field there would change what the widget says
   * rather than keep it.
   */
  adoptsMoved?: false;
  /**
   * Why, and when, the canvas does not draw this well.
   *
   * Declared rather than hidden: the well stays configurable and its value is
   * saved, and a control that silently does nothing reads as a bug. What this
   * adds over a flag is the **reason**, which the panel prints next to the field.
   * `wellAvailability` is what reads it.
   *
   * Absent means the well is drawn.
   */
  drawRule?: WellDrawRule;
  /**
   * Whether the measure in this well is a series of its own, though the well
   * takes only one.
   *
   * The Line's secondary axis: one field, and one more line on the plot. It
   * counts as a measure wherever the answer to "is there a single series?"
   * matters — small multiples split **one** series into panels, and a second line
   * that the endpoint never splits would have no panel to be drawn in.
   */
  addsSeries?: boolean;
  /**
   * The i18n leaf of the hint shown **instead of** `hint` while the well is empty.
   *
   * For a well whose empty state is itself a consequence the user should not
   * miss: a scatter without Values collapses into one point. The panel prints it
   * emphasised, and swaps back to `hint` once there is a field.
   */
  emptyHint?: string;
  /**
   * The most fields the well may hold, when the endpoint caps it.
   *
   * Only on a `multiple` well. The Tooltips list is the one case: more than
   * `MAX_TOOLTIP_MEASURES` is a 422, so the picker stops there instead of
   * building a request that cannot be answered.
   */
  maxFields?: number;
  /**
   * Whether the well's measures annotate the series rather than draw one.
   *
   * The Tooltips list: several measures, like a Y axis, but shown on hover and
   * never plotted. Declared so "which well holds the widget's measures" is not
   * answered with the tooltips on a type whose values well takes one field (pie,
   * scatter, funnel).
   */
  annotates?: boolean;
}

/** A visualisation, as the catalogue declares it. */
export interface DashboardWidgetTypeSpec {
  /** Wells in the order the Datos section draws them. */
  wells: DashboardWell[];
  /**
   * Which of the Formato controls this visualisation answers to (SL-1665).
   *
   * Declared per type, as data, for the same reason the wells are: **nothing
   * downstream branches on the widget's type**. A `if (type === 'kpi')` in the
   * Formato section is the one thing that would make a fourteenth type cost
   * more than a row — and it would be the second copy of a rule the catalogue
   * already holds.
   *
   * Absent means the control is not offered. A legend on a KPI would be a
   * legend of one series; a heat map on a bar chart has no cells to tint.
   */
  format?: DashboardFormatCapabilities;
}

/** Which Formato controls a visualisation offers. */
export interface DashboardFormatCapabilities {
  /**
   * Whether it draws a legend — true wherever several series can share the plot.
   *
   * Not derived from "has a `legend` well": a scatter has one and still shows a
   * legend, while a table has none and shows nothing. What the control asks is
   * whether there is a legend to *place*, which is a property of the drawing,
   * not of the wells.
   */
  legend?: boolean;
  /** Whether it tints its cells by value — the matrix's heat map, and only its. */
  colorScale?: boolean;
  /** Whether it draws axes and a grid that can be turned off — the plots on X/Y. */
  axes?: boolean;
}

// ---------------------------------------------------------------------------
// Wells shared by several types. Declared once so a change to what "Legend"
// means cannot reach six visualisations and miss the seventh.
// ---------------------------------------------------------------------------

/**
 * Legend: one series per value of a dimension.
 *
 * Optional on every cartesian type, stacked ones included. With a legend the
 * series come from its values and **only the first measure is drawn** — a rule
 * of the PRD (§7) that the panel has to say out loud, because dropping the rest
 * in silence reads as the chart having lost data.
 */
const LEGEND: DashboardWell = {
  key: 'legend',
  accepts: 'dimension',
  multiple: false,
  required: false,
  hint: 'legend',
  legendNote: true,
};

/**
 * Splits the visual into a grid, one panel per value.
 *
 * Its own request field since SL-1826 (`smallMultiples` → `panels`): each panel
 * is the whole chart over that value's rows, Legend and several measures
 * included, so nothing else in the widget blocks it.
 */
const SMALL_MULTIPLES: DashboardWell = {
  key: 'smallMultiples',
  accepts: 'dimension',
  multiple: false,
  required: false,
  hint: 'smallMultiples',
  adoptsMoved: false,
};

/**
 * Extra measures shown on hover (SL-1824).
 *
 * Several, as in Power BI: the endpoint takes `tooltips` as a list and answers
 * one series per measure (a `tooltip` array per slice, stage or point), never
 * split by the Legend. Capped at `MAX_TOOLTIP_MEASURES`, the endpoint's own
 * limit. Every type that declares this well computes it; KPI, matrix and table
 * do not declare it, and the endpoint rejects it there.
 */
const TOOLTIPS: DashboardWell = {
  key: 'tooltips',
  accepts: 'measure',
  multiple: true,
  required: false,
  hint: 'tooltips',
  maxFields: MAX_TOOLTIP_MEASURES,
  annotates: true,
  adoptsMoved: false,
};

/**
 * A cartesian's tooltips, which yield to small multiples.
 *
 * The endpoint computes `tooltips` for the whole chart and not per panel
 * (`WidgetPanel` carries none), so a panel has nothing to show for the bar under
 * the cursor. Printing the whole chart's figure there would be a wrong number;
 * dropping it without a word reads as a bug. Saved, marked, and not sent.
 */
const CARTESIAN_TOOLTIPS: DashboardWell = {
  ...TOOLTIPS,
  drawRule: { kind: 'notWithWell', wellKey: 'smallMultiples', reason: 'tooltipsNotInPanels' },
};

/** The three optional wells every cartesian type carries. */
const CARTESIAN_EXTRAS: DashboardWell[] = [LEGEND, SMALL_MULTIPLES, CARTESIAN_TOOLTIPS];

/**
 * The Formato controls of anything that plots several series.
 *
 * Every cartesian and circular type, plus the scatter and the funnel: they draw
 * a legend and nothing else conditional. Declared once so adding a fourteenth
 * plotted type is a reference rather than a copy — the same reason
 * `CARTESIAN_EXTRAS` exists.
 */
const PLOTTED: DashboardFormatCapabilities = { legend: true };
/** A plot on X and Y: its axes and grid can be hidden too. */
const ON_AXES: DashboardFormatCapabilities = { legend: true, axes: true };

/**
 * A vertical cartesian: a dimension across, measures up.
 *
 * `xHint` because what the X axis means changes with the shape: a line runs
 * over time, a bar draws one column per value.
 */
const verticalCartesian = (xHint: string): DashboardWell[] => [
  { key: 'xAxis', accepts: 'dimension', multiple: false, required: true, hint: xHint },
  { key: 'yAxis', accepts: 'measure', multiple: true, required: true },
  ...CARTESIAN_EXTRAS,
];

/** A horizontal cartesian: the dimension runs down the Y axis instead. */
const horizontalCartesian = (): DashboardWell[] => [
  { key: 'yAxis', accepts: 'dimension', multiple: false, required: true, hint: 'oneBarPerValue' },
  { key: 'xAxis', accepts: 'measure', multiple: true, required: true },
  ...CARTESIAN_EXTRAS,
];

/**
 * A part-of-whole: one measure, split by an optional dimension.
 *
 * Its dimension is **keyed** `category` and **labelled** "Legend" — the key is
 * where saved widgets keep it, the label is what it does.
 */
const partOfWhole = (): DashboardWell[] => [
  { key: 'values', accepts: 'measure', multiple: false, required: true },
  {
    key: 'category',
    labelKey: 'legend',
    accepts: 'dimension',
    multiple: false,
    required: false,
    hint: 'oneSlicePerValue',
  },
  TOOLTIPS,
];

/**
 * The catalogue.
 *
 * Well order is display order: the panel renders them top to bottom as they are
 * listed, so required wells come first and the eye meets what blocks the widget
 * before what decorates it. The order of the types themselves is the order of
 * the picker — see `WIDGET_TYPE_ORDER`.
 */
export const WIDGET_TYPES: Record<DashboardWidgetType, DashboardWidgetTypeSpec> = {
  kpi: {
    wells: [
      { key: 'values', accepts: 'measure', multiple: false, required: true },
      {
        key: 'categories',
        accepts: 'dimension',
        multiple: false,
        required: false,
        hint: 'kpiCategories',
      },
    ],
    // No legend: a KPI draws one number, so there would be one series to name.
  },
  line: {
    wells: [
      { key: 'xAxis', accepts: 'dimension', multiple: false, required: true, hint: 'usuallyADate' },
      { key: 'yAxis', accepts: 'measure', multiple: true, required: true },
      // Only Line carries a second axis: it is what makes two measures on
      // different scales readable together. The endpoint does not repeat it per
      // panel (`WidgetPanel`), so with small multiples it is saved but not drawn.
      {
        key: 'secondaryYAxis',
        adoptsMoved: false,
        accepts: 'measure',
        multiple: false,
        required: false,
        addsSeries: true,
        drawRule: {
          kind: 'notWithWell',
          wellKey: 'smallMultiples',
          reason: 'secondaryAxisNotInPanels',
        },
      },
      ...CARTESIAN_EXTRAS,
    ],
    format: ON_AXES,
  },
  area: { wells: verticalCartesian('usuallyADate'), format: ON_AXES },
  bar: { wells: verticalCartesian('oneColumnPerValue'), format: ON_AXES },
  horizontalBar: { wells: horizontalCartesian(), format: ON_AXES },
  stackedBar: { wells: verticalCartesian('oneColumnPerValue'), format: ON_AXES },
  stackedHorizontalBar: { wells: horizontalCartesian(), format: ON_AXES },
  pie: { wells: partOfWhole(), format: PLOTTED },
  donut: { wells: partOfWhole(), format: PLOTTED },
  scatter: {
    wells: [
      // Both axes are measures here — the only type where that is true — and
      // both are numeric-only: a point needs a coordinate, and a name has none.
      { key: 'xAxis', accepts: 'measure', multiple: false, required: true, numericOnly: true },
      { key: 'yAxis', accepts: 'measure', multiple: false, required: true, numericOnly: true },
      // Without Values every row collapses into a single point: this is what
      // gives the plot its granularity, one point per value (PRD Apéndice A).
      {
        key: 'values',
        accepts: 'dimension',
        multiple: true,
        required: false,
        hint: 'scatterValues',
        emptyHint: 'scatterValuesEmpty',
      },
      // Its own legend and not the shared one: here the two measures are the
      // axes, so "only the first measure is drawn" does not apply and showing
      // that warning would describe a rule this type does not have.
      {
        key: 'legend',
        accepts: 'dimension',
        multiple: false,
        required: false,
        hint: 'scatterLegend',
      },
      { key: 'size', accepts: 'measure', multiple: false, required: false, adoptsMoved: false },
      // One frame per value, in play order (SL-1827): the canvas steps through
      // them with a play-axis control.
      {
        key: 'playAxis',
        accepts: 'dimension',
        multiple: false,
        required: false,
        hint: 'playAxis',
        adoptsMoved: false,
      },
      TOOLTIPS,
    ],
    format: ON_AXES,
  },
  matrix: {
    wells: [
      {
        key: 'rows',
        accepts: 'dimension',
        multiple: false,
        required: true,
        hint: 'oneRowPerValue',
      },
      // ⚠️ The one place an optional well comes before a required one. Rows ×
      // Columns is the shape of a crosstab and reads as a pair; Values between
      // them splits the thing the user is describing. Declared as the exception
      // it is, so the catalogue's own test can allow it by name.
      {
        key: 'columns',
        accepts: 'dimension',
        multiple: false,
        required: false,
        hint: 'matrixColumns',
      },
      { key: 'values', accepts: 'measure', multiple: true, required: true, hint: 'matrixValues' },
    ],
    // The heat map is what a matrix is for, and the only type that has cells to
    // tint. No legend: a crosstab labels its own rows and columns.
    format: { colorScale: true },
  },
  table: {
    wells: [
      // One well, dimensions and measures mixed, and the order they are added
      // **is** the order of the columns (PRD Apéndice A).
      { key: 'columns', accepts: 'any', multiple: true, required: true },
    ],
    // A table has headers, not a legend.
  },
  funnel: {
    // One stage per value of the category, in stage order (SL-1825): a select
    // column's option order, otherwise first appearance. Never sorted by value.
    wells: [
      { key: 'category', accepts: 'dimension', multiple: false, required: true },
      { key: 'values', accepts: 'measure', multiple: false, required: true },
      TOOLTIPS,
    ],
    format: PLOTTED,
  },
};

/**
 * Types whose wells are deliberately not ordered required-first.
 *
 * One entry, and it carries its reason: see `matrix` above. A list and not a
 * flag on the well so the exception is visible from the catalogue's top level —
 * an exception nobody can find is how a rule quietly stops being one.
 */
export const UNORDERED_WELL_TYPES: DashboardWidgetType[] = ['matrix'];

/**
 * The types offered by the Tipo section, in display order.
 *
 * Written out rather than derived from `WIDGET_TYPES`, because this is the
 * order of the picker and object key order is not something to lean on. It
 * follows the prototype's gallery: the single number first, then trends, then
 * comparisons, then parts of a whole, then the dense ones.
 */
export const WIDGET_TYPE_ORDER: DashboardWidgetType[] = [
  'kpi',
  'line',
  'area',
  'bar',
  'horizontalBar',
  'stackedBar',
  'stackedHorizontalBar',
  'pie',
  'donut',
  'scatter',
  'matrix',
  'funnel',
  'table',
];

/** The wells a type declares, or none when no type has been chosen yet. */
export const wellsOf = (type: DashboardWidgetType | undefined): DashboardWell[] =>
  type ? WIDGET_TYPES[type].wells : [];

/**
 * The Formato controls a type offers.
 *
 * Empty without a type: with no visualisation chosen there is nothing to format
 * conditionally, and the section still shows what every widget has — palette,
 * numbers, description.
 */
export const formatCapabilitiesOf = (
  type: DashboardWidgetType | undefined,
): DashboardFormatCapabilities => (type ? (WIDGET_TYPES[type].format ?? {}) : {});

/** One well of a type, by key. `undefined` when the type does not declare it. */
export const wellOf = (
  type: DashboardWidgetType | undefined,
  wellKey: string,
): DashboardWell | undefined => wellsOf(type).find((well) => well.key === wellKey);

/**
 * Whether a well's contents carry a calculation.
 *
 * `any` counts: the Table's single well mixes dimensions and measures, and a
 * numeric column dropped in it is a measure like anywhere else.
 */
export const wellTakesMeasures = (well: DashboardWell): boolean => well.accepts !== 'dimension';

/**
 * Calculations valid over any column, whatever its type (PRD Apéndice A).
 *
 * Counting asks "how many rows", which every column can answer — a text column
 * has a count of distinct values and a count of blanks just as a numeric one
 * does.
 */
export const COUNTING_CALCULATIONS: DashboardCalculation[] = [
  'count',
  'countDistinct',
  'filled',
  'empty',
  'percentFilled',
  'percentEmpty',
];

/**
 * Calculations that only make sense over a numeric column (PRD Apéndice A).
 *
 * Offered only when the chosen column is numeric — not offered and then
 * refused. A control that lists Suma over a text column has already made the
 * user's mistake for them.
 */
export const VALUE_CALCULATIONS: DashboardCalculation[] = [
  'sum',
  'average',
  'median',
  'min',
  'max',
  'p95',
];

/**
 * The calculations offered for a column, the value ones first where they apply.
 *
 * Order matters: over a number, Suma is what the user came for, so it opens the
 * menu. Over anything else the counting family is the whole menu.
 *
 * `isNumeric` and not the column's raw type on purpose: what the rule turns on
 * is one question, and the mapping from the table's type vocabulary to that
 * question belongs where the schema is read, not here.
 */
export const calculationsFor = (isNumeric: boolean): DashboardCalculation[] =>
  isNumeric ? [...VALUE_CALCULATIONS, ...COUNTING_CALCULATIONS] : [...COUNTING_CALCULATIONS];

/**
 * The calculation a column starts on when it is first placed in a well.
 *
 * A number opens on Suma and everything else on Conteo — the same call the
 * prototype makes, and the one that is right more often than not: a column of
 * amounts is placed to be totalled, a column of names to be counted.
 */
export const defaultCalculationFor = (isNumeric: boolean): DashboardCalculation =>
  isNumeric ? 'sum' : 'count';
